/**
 * Translations for the jobs module and the sidebar navigation. English is the
 * source; the Vietnamese catalog is the one the app ships with.
 */
export const englishJobMessages = {
  name: "Job |||| Jobs",
  list: {
    title: "Measurements & orders",
    subtitle: "Newest jobs first",
    search_placeholder: "Search customer, phone, address, code…",
    showing: "Showing %{count} jobs",
    kpi_total: "Total jobs",
    kpi_total_hint: "%{count} signed orders",
    kpi_revenue: "Signed revenue",
    kpi_revenue_hint: "Awaiting signature: %{amount}",
    kpi_collected: "Collected",
    kpi_collected_hint: "%{percent}% of signed revenue",
    kpi_receivable: "Outstanding receivables",
    kpi_receivable_hint: "%{count} jobs not fully paid",
    filter_status: "Job status",
    filter_payment: "Payment",
    filter_sales_rep: "Sales lead",
    filter_care_rep: "Customer care",
    filter_month: "Month",
    filter_line: "Product line",
    all_statuses: "All statuses",
    all_payments: "All payments",
    all_staff: "Everyone",
    all_months: "All months",
    all_lines: "All product lines",
    clear_filters: "Clear filters",
    col_customer: "Customer & address",
    col_products: "Products",
    col_date_staff: "Date & staff",
    col_status: "Progress",
    col_payment: "Payment",
    col_total: "Total & remaining",
    sales_rep_short: "Lead:",
    care_rep_short: "Care:",
    remaining: "Left: %{amount}",
    paid_in_full: "Paid in full",
    deposit_amount: "Deposit %{amount}",
    show_more: "Show %{count} more",
    empty: "No job matches these filters.",
    new_job: "New job",
    export: "Export CSV",
    import: "Import from Google Sheet",
  },
  import: {
    title: "Import from the Google Sheet",
    description:
      "In the sheet, open each tab (CongTrinh, LichSu, CaiDat) and use File > Download > CSV, then pick the files here. Existing jobs are matched by code and only updated when the sheet holds a newer version, so jobs edited in the app are never overwritten.",
    choose_files: "CSV files",
    start: "Import",
    running: "Importing… %{done} / %{total}",
    done: "Imported %{jobs} jobs, %{versions} versions and %{settings} settings. %{skipped} jobs already edited in the app were kept.",
    unknown_file: "%{name}: this is not a CongTrinh, LichSu or CaiDat export.",
    error: "Import failed: %{message}",
  },
  show: {
    back: "Job list",
    open_legacy: "Open in quote tool",
    doors: "%{count} openings",
    products: "%{count} products",
    version: "Version %{version} · %{date}",
    steps: {
      measure: "Measure",
      quote: "Quote",
      order: "Order",
      docs: "Documents",
      tracking: "Tracking & payment",
    },
  },
  editor: {
    edit: "Edit job",
    create_title: "New job",
    edit_title: "Edit %{code}",
    customer_title: "Customer & site visit",
    customer: "Customer name",
    phone: "Phone",
    zalo: "Zalo",
    address: "Site address",
    measurer: "Measured by",
    measure_date: "Measured on",
    rows_title: "Measurements",
    add_row: "Add row",
    duplicate: "Duplicate row",
    remove: "Delete row",
    col_variant: "Variant",
    col_spec: "Sewing / frame",
    col_layout: "Panels / control",
    no_rows: "No row yet. Add the first measured opening.",
    item_name: "Item name",
    prices_title: "Prices by product group",
    use_book: "Fill from price book (%{count})",
    price_mode: "Pricing",
    price_mode_shared: "One price (fabric + sheer)",
    price_mode_split: "Separate fabric and sheer",
    supplier: "Supplier",
    group_total: "Group total",
    no_groups: "Prices appear here once rows are measured.",
    extras_title: "Other items",
    add_extra: "Add item",
    extra_name: "Item",
    extra_spec: "Detail",
    terms_title: "Quote terms",
    quote_date: "Quote date",
    sales: "Sales",
    discount: "Discount",
    vat: "VAT %",
    deposit: "Deposit",
    notes: "Terms & notes",
    save: "Save job",
    cancel: "Cancel",
    saved: "Job %{code} saved",
    conflict:
      "Someone else saved this job meanwhile (version %{version}). Reload the page to see their changes before editing.",
    save_error: "Could not save: %{message}",
    version_note_create: "Created in the app",
    version_note_edit: "Edited in the app",
    unsaved: "Unsaved changes",
  },
  ocr: {
    button: "Read from sheet photo (AI)",
    title: "Read measurements from a sheet photo",
    hint: "Take or pick a clear, straight, well-lit photo of the handwritten measurement sheet.",
    choose: "Take / choose photo",
    reading: "Reading the sheet… %{seconds}s",
    reading_hint: "Usually 10–40 seconds. It stops by itself after 2 minutes.",
    cancel: "Cancel",
    retry: "Try another photo",
    review: "%{count} rows read. Fields marked in amber need checking.",
    customer: "Customer on the sheet",
    apply: "Add %{count} rows to the job",
    applied: "%{count} rows added from the photo. Check them before saving.",
    no_rows: "No measured row could be read from this photo.",
    error_NOKEY:
      "Photo reading is not set up yet: the administrator must add the Gemini key (GEMINI_API_KEY).",
    error_QUOTA:
      "Today's free photo-reading quota is used up. It resets in the afternoon (Vietnam time); type the rows in meanwhile.",
    error_TIMEOUT:
      "The AI took too long to answer. Try again, or a smaller / clearer photo.",
    error_UPSTREAM:
      "The photo could not be read. Retake it clearer, well lit and straight.",
    error_IMAGE: "This file could not be opened as an image.",
  },
  measure: {
    survey_title: "Site survey",
    phone: "Phone",
    address: "Address",
    zalo: "Zalo",
    date: "Measured on",
    measurer: "Measured by",
    progress_title: "Measuring progress",
    progress_hint: "%{done} / %{total} openings measured",
    warnings:
      "%{count} rows are missing technical data (size or product code).",
    table_title: "Site measurements",
    unit_hint: "Sizes in metres · area and running metres are computed",
    col_position: "Position",
    col_type: "Product",
    col_code: "Code",
    col_sewing: "Sewing / frame",
    col_sheer: "Sheer layer",
    col_mount: "Mounting",
    col_width: "Width",
    col_height: "Height",
    col_qty: "Qty",
    col_billable: "Billable",
    col_state: "State",
    col_note: "Note",
    state_ok: "Measured",
    state_missing_size: "Missing size",
    state_missing_code: "Missing code",
    no_rows:
      "This job was imported from the 2026 job file and has no measurement rows. Its totals are kept as entered.",
  },
  quote: {
    lines_title: "Quote breakdown",
    col_item: "Item & specification",
    col_unit: "Unit",
    col_unit_price: "Unit price",
    col_amount: "Amount",
    info_title: "Quote details",
    date: "Quote date",
    sales: "Sales",
    notes: "Terms & notes",
    totals_title: "Quote total",
    subtotal: "Goods total",
    discount: "Discount",
    vat: "VAT (%{rate}%)",
    total: "TOTAL",
    deposit: "Deposit",
    remaining: "Due on handover",
    none: "None",
    extras: "Other items",
    mismatch:
      "The recalculated total differs from the saved total (%{amount}); the saved total is used everywhere else.",
    legacy_actions:
      "Quote PDF, Google Sheet and Zalo image are still produced in the current quote tool.",
    no_lines: "No priced line on this job.",
  },
  order: {
    title: "Factory order",
    ordered: "Order placed",
    not_ordered: "Not sent to the factory yet",
    order_date: "Order date",
    need_by: "Needed by",
    contact: "Contact",
    delivery: "Delivery address",
    note: "Delivery & handover note",
    factory_title: "Order sheet by supplier",
    factory_hint: "Factory copy: sizes, specs and mounting only, never prices.",
    supplier: "Supplier: %{name}",
    unassigned: "Supplier not assigned",
  },
  docs: {
    title: "Contract & legal documents",
    contract_no: "Contract no.",
    party_a: "Party A: customer",
    customer: "Customer",
    phone: "Phone",
    address: "Site & invoicing address",
    total: "Contract value",
    in_words: "In words",
    templates: "Document templates",
    contract: "Sales contract + appendix",
    acceptance: "Handover acceptance record",
    payment_request: "Payment request",
    legacy_hint:
      'Google Docs and PDF documents are still generated in the current quote tool, which files them in the Drive folder "HKH - Hồ sơ".',
  },
  tracking: {
    timeline_title: "Job progress",
    payment_title: "Payment status",
    total: "Job value",
    collected: "Collected",
    remaining: "Still to collect",
    progress: "%{percent}% collected",
    edit_title: "Tracking & staff",
    status: "Status",
    payment_status: "Payment",
    deposit: "Collected so far (VND)",
    sales_rep: "Sales lead",
    care_rep: "Customer care",
    source: "Customer source",
    start_date: "Start date",
    end_date: "End date",
    notes: "Notes",
    save: "Save",
    saved: "Job updated",
    history_title: "Version history",
    history_empty: "No saved version yet.",
    version_label: "Version %{version}",
  },
};

export const englishNavMessages = {
  tagline: "Interior solutions",
  section_lines: "Product lines",
  section_admin: "Administration",
  dashboard: "Overview",
  jobs: "Jobs",
  contacts: "Customers (CRM)",
  companies: "Companies",
  deals: "Pipeline",
  price_book: "Products & price book",
  marketing: "Marketing & promotions",
  finance: "Income, expenses & finance",
  users: "Staff & users",
  settings: "Settings",
  collapse: "Collapse menu",
  expand: "Expand menu",
  coming_soon: "Coming soon",
  lines: {
    curtain: "Curtains & tailoring",
    mattress: "Mattresses & foam",
    bedding: "Bedding",
    screen: "Insect screens",
    dryer: "Clothes dryers",
  },
};
